import React from 'react';
import classNames from 'classnames';

import { isSellerUserType } from '../../config/userRoles';
import { intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';

import { FieldTextInput } from '../../components';

/**
 * A component that renders the display name field.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.displayName
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {string} props.formName - The form name
 * @param {propTypes.userType} props.userTypeConfig - The user type config
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const UserFieldDisplayName = props => {
  const { rootClassName, className, formId, formName, userTypeConfig, intl } = props;

  const { displayInSignUp, required } = userTypeConfig?.displayNameSettings || {};
  const isDisabled = userTypeConfig?.defaultUserFields?.displayName === false;
  // The display name is what shoppers see on listings and profiles, so sellers are
  // always asked for it (as their brand name) instead of it falling back to the
  // account holder's first name + last initial.
  const isSeller = isSellerUserType(userTypeConfig?.userType);
  const isAllowedInSignUp = displayInSignUp === true || isSeller;

  if (isDisabled || !isAllowedInSignUp) {
    return null;
  }

  const isRequired = required === true || isSeller;
  const keyPrefix = isSeller ? `${formName}.brandName` : `${formName}.displayName`;
  const validateMaybe = isRequired
    ? {
        validate: validators.required(
          intl.formatMessage({
            id: `${keyPrefix}Required`,
          })
        ),
      }
    : {};

  return (
    <FieldTextInput
      className={classNames(className, { [rootClassName]: !!rootClassName })}
      type="text"
      id={formId ? `${formId}.displayName` : 'displayName'}
      name="displayName"
      label={intl.formatMessage({
        id: `${keyPrefix}Label`,
      })}
      placeholder={intl.formatMessage({
        id: `${keyPrefix}Placeholder`,
      })}
      {...validateMaybe}
    />
  );
};

export default UserFieldDisplayName;
